import { observer } from "mobx-react-lite";
import { useCallback } from "react";
import { useParams } from "react-router";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { useServerTable } from "@/hooks/useServerTable";
import { useStore } from "@/hooks/useStore";
import { structureProperty, StructurePropertyListItem } from "@/endpoints/dryp/structureProperty";
import { List } from "@/components/List";
import { CUSTOM_FILTERS } from "@/config/list";
import { getTableSearchParam } from "@/utils/filters";
import { usePropertyColumns } from "./Columns";
import { PropertyHasValueToggle, getTablePropertyHasValueFilter } from "./PropertyHasValueToggle";

export const Properties = observer(() => {
	const { id } = useParams<{ id: string }>();
	const structureId = id as Dryp.StructureId;
	const propertiesApi = useAsyncStateStore(structureProperty.getForStructure, { debounce: 300 });

	const {
		uiStore: { modalStore },
	} = useStore();

	const handleAddOrUpdateProperty = useCallback(
		(property: StructurePropertyListItem) => {
			modalStore.open("addOrUpdateStructureProperty", {
				structureId,
				property,
				onSuccess: () => propertiesApi.refetch(),
			});
		},
		[modalStore, structureId, propertiesApi],
	);

	const columns = usePropertyColumns(handleAddOrUpdateProperty);

	const { table } = useServerTable({
		columns,
		data: propertiesApi.data?.items || [],
		fetchFn: params =>
			propertiesApi.fetch(
				structureId,
				{ pageNumber: params.page, pageSize: params.pageSize },
				params.sort,
				getTableSearchParam(params?.filters),
				{ hasValue: getTablePropertyHasValueFilter(params?.filters) },
			),
		initialValues: {
			page: 0,
			pageSize: 20,
		},
	});

	return (
		<div className="flex flex-col gap-4 md:gap-6 p-4 md:p-6">
			<header className="flex items-center justify-between gap-4">
				<h1 className="text-2xl md:text-4xl font-semibold tracking-tight">Properties</h1>
			</header>

			<List
				table={table}
				pagination={{
					total: propertiesApi.data?.query?.pagination.totalItems || 0,
				}}
				toolbar={{
					filterKey: CUSTOM_FILTERS.search,
					customFilter: <PropertyHasValueToggle table={table} />,
				}}
				layout={{
					loading: propertiesApi.loading,
				}}
			/>
		</div>
	);
});
